// src/components/common/SenhaObrigatoriaModal.tsx
// Modal de TROCA OBRIGATÓRIA de senha (issue #27). Bloqueante (closable={false}):
// só sai por "Trocar senha" (sucesso) ou "Sair" (logout). Espelha o TermsModal:
// presentational puro — a submissão (PUT minha-senha) vive no
// SenhaObrigatoriaContext; aqui ficam só os campos e a decisão de habilitar.
//
// Dois textos por `motivo`:
//  - primeiro_acesso: a senha do e-mail é provisória e precisa ser substituída.
//  - expirada: a senha venceu (validade de 3 meses).
import React, { useState } from 'react';
import Modal from './Modal';
import PasswordRequirements from './PasswordRequirements';
import { evaluatePassword } from '@/utils/passwordRules';
import type { SenhaMotivo } from '@/types/senha';
import './SenhaObrigatoriaModal.css';

interface SenhaObrigatoriaModalProps {
  isOpen: boolean;
  theme: 'dark' | 'light';
  motivo: SenhaMotivo;
  padraoExigido: string;
  submitting: boolean;
  errorMsg: string | null;
  onSubmit: (senhaAtual: string, novaSenha: string) => void;
  onLogout: () => void;
}

const TEXTOS: Record<SenhaMotivo, { titulo: string; explicacao: string }> = {
  primeiro_acesso: {
    titulo: 'Defina sua senha',
    explicacao:
      'A senha que você recebeu por e-mail é provisória. Defina uma nova senha para continuar.',
  },
  expirada: {
    titulo: 'Troca de senha obrigatória',
    explicacao:
      'Sua senha expirou (validade de 3 meses). Defina uma nova senha para continuar.',
  },
};

const SenhaObrigatoriaModal: React.FC<SenhaObrigatoriaModalProps> = ({
  isOpen,
  theme,
  motivo,
  padraoExigido,
  submitting,
  errorMsg,
  onSubmit,
  onLogout,
}) => {
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');

  const { allMet } = evaluatePassword(novaSenha);
  const senhasConferem = novaSenha.length > 0 && novaSenha === confirmar;
  const podeEnviar = !!senhaAtual && allMet && senhasConferem && !submitting;

  const { titulo, explicacao } = TEXTOS[motivo];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeEnviar) return;
    onSubmit(senhaAtual, novaSenha);
  };

  return (
    // Gate bloqueante: closable={false} — não fecha por overlay nem X.
    <Modal isOpen={isOpen} theme={theme} closable={false} onClose={() => {}}>
      <form className="senha-obrigatoria-modal" onSubmit={handleSubmit} noValidate>
        <h3 className="senha-obrigatoria-title">{titulo}</h3>
        <p className="senha-obrigatoria-explanation">{explicacao}</p>

        <label className="senha-obrigatoria-label">
          Senha atual
          <input
            type="password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            autoComplete="current-password"
            disabled={submitting}
          />
        </label>

        <label className="senha-obrigatoria-label">
          Nova senha
          <input
            type="password"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            autoComplete="new-password"
            disabled={submitting}
          />
        </label>

        <PasswordRequirements senha={novaSenha} padraoExigido={padraoExigido} />

        <label className="senha-obrigatoria-label">
          Confirmar nova senha
          <input
            type="password"
            value={confirmar}
            onChange={(e) => setConfirmar(e.target.value)}
            autoComplete="new-password"
            disabled={submitting}
          />
        </label>
        {confirmar.length > 0 && !senhasConferem && (
          <p className="senha-obrigatoria-inline-error">As senhas não coincidem.</p>
        )}

        {errorMsg && (
          <p className="senha-obrigatoria-error" role="alert">
            {errorMsg}
          </p>
        )}

        <div className="senha-obrigatoria-buttons">
          <button
            type="button"
            className="senha-obrigatoria-button senha-obrigatoria-button-logout"
            onClick={onLogout}
            disabled={submitting}
          >
            Sair
          </button>
          <button
            type="submit"
            className="senha-obrigatoria-button senha-obrigatoria-button-submit"
            disabled={!podeEnviar}
          >
            {submitting ? 'Trocando…' : 'Trocar senha'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SenhaObrigatoriaModal;
